import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useResendRateLimit } from "@/hooks/useResendRateLimit";
import MainLayout from "@/components/layout/MainLayout";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showVerificationSection, setShowVerificationSection] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { user } = useAuth();
  const {
    isRateLimited,
    isLimitReached,
    remainingAttempts,
    recordSendAttempt,
    getStatusMessage
  } = useResendRateLimit('verification');

  // Redirect if already logged in
  if (user) {
    navigate("/");
  }

  // 檢查個人資料是否完整
  const checkProfileCompleteness = async (userId: string) => {
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('full_name, nickname, country, is_verified')
        .eq('id', userId)
        .single();

      console.log('Profile data check:', profileData, error);

      // 如果沒有個人資料或資料不完整，導向完成註冊頁面
      if (error || !profileData?.full_name || !profileData?.nickname || !profileData?.country) {
        console.log('Profile incomplete, redirecting to signup complete');
        navigate('/signup/complete');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking profile:', error);
      navigate('/signup/complete');
      return false;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setShowVerificationSection(false);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        // Check if the error is related to email not confirmed
        if (error.message.includes('Email not confirmed') || error.message.includes('not verified')) {
          setShowVerificationSection(true);
          setError(t('auth.emailNotVerified'));
        } else if (error.message.includes('Invalid login credentials')) {
          setError(t('auth.invalidCredentials'));
        } else {
          setError(error.message);
        }
        throw error;
      }

      if (data.user) {
        // 檢查個人資料是否完整
        const isProfileComplete = await checkProfileCompleteness(data.user.id);
        
        if (isProfileComplete) {
          toast({
            title: t('auth.loginSuccess'),
            description: t('auth.welcomeBack')
          });
          navigate("/");
        }
      }
    } catch (error: any) {
      console.error("Login error:", error);
      if (!error.message.includes('Email not confirmed') && !error.message.includes('not verified')) {
        toast({
          variant: "destructive",
          title: t('auth.loginFailed'),
          description: error.message.includes('Invalid login credentials') ? t('auth.invalidCredentials') : error.message
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError("");

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/signup/complete`
        }
      });
      if (error) throw error;
    } catch (error: any) {
      console.error("Google login error:", error);
      setError(error.message);
      toast({
        variant: "destructive",
        title: t('auth.loginFailed'),
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError(t('auth.pleaseEnterValidEmail'));
      return;
    }

    if (isRateLimited || isLimitReached) {
      const statusMessage = getStatusMessage();
      if (statusMessage) {
        toast({
          variant: "destructive",
          title: t('auth.verificationEmailFailed'),
          description: statusMessage
        });
      }
      return;
    }

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email
      });

      if (error) throw error;

      // Record the send attempt for rate limiting
      recordSendAttempt();
      
      toast({
        title: t('auth.verificationEmailSent'),
        description: t('auth.checkEmailForVerification')
      });
    } catch (error: any) {
      console.error("Resend verification error:", error);
      toast({
        variant: "destructive",
        title: t('auth.verificationEmailFailed'),
        description: error.message
      });
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <MainLayout>
      <div className="container flex items-center justify-center min-h-screen py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">會員登入</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <form onSubmit={handleLogin}>
              <div className="grid gap-2">
                <Input
                  type="email"
                  placeholder="電子郵件"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Input
                  type="password"
                  placeholder="密碼"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {showVerificationSection && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800 mb-3">
                    請先去信箱點選驗證信以完成帳號驗證
                  </p>
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600">未收到驗證信嗎？</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleResendVerification}
                      disabled={isRateLimited || isLimitReached}
                      className="w-full"
                    >
                      {t('auth.resendVerificationEmail')}
                    </Button>
                    {statusMessage && (
                      <p className="text-xs text-red-600">{statusMessage}</p>
                    )}
                    {!isLimitReached && remainingAttempts < 5 && (
                      <p className="text-xs text-gray-500">
                        {t('auth.remainingAttempts', { count: remainingAttempts })}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              <Button
                disabled={isLoading}
                type="submit"
                className="w-full mt-4 bg-orange-500 hover:bg-orange-400"
              >
                {isLoading ? '登入中...' : '登入'}
              </Button>
            </form>
            
            <div className="flex items-center justify-center">
              <Button variant="link" onClick={handleForgotPassword}>
                忘記密碼？
              </Button>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  或者
                </span>
              </div>
            </div>
            
            <Button
              variant="outline"
              disabled={isLoading}
              className="w-full flex items-center gap-2"
              onClick={handleGoogleLogin}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              使用 Google 帳號登入
            </Button>
            
            <div className="text-sm text-muted-foreground text-center">
              還沒有帳號？ <Link to="/signup" className="text-primary">建立新帳號</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Login;
